import { useCallback, useRef } from 'react';

import { type SearchPreloadDataProvider } from '../../../common/types/search-preload';
import { onPreloadQueryVarsDiff } from '../../../common/utils/events/preload-query-vars-diff-event';
import { useCommonAndPrivateAttributes, useAnalytics } from '../../../controllers/analytics';

export type PreloadQueryVarsDiff = {
	match: boolean;
	mismatchCount: number;
	mismatchFields: string[];
};

export type PreloadQueryVarsDiffReport = {
	match: boolean;
	mismatchCount: number;
	search: PreloadQueryVarsDiff;
	rightPanel: PreloadQueryVarsDiff;
};

const ROOT_PATH = '';
const ALLOWED_DIFF_KEYS = new Set(['sessionId', 'expectResult']);

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getObjectKeys(value: Record<string, unknown>) {
	return Object.keys(value).sort();
}

function buildPath(basePath: string, key: string) {
	return basePath ? `${basePath}.${key}` : key;
}

function buildArrayPath(basePath: string, index: number) {
	return `${basePath}[${index}]`;
}

function hasOwnKey(value: Record<string, unknown>, key: string) {
	return Object.prototype.hasOwnProperty.call(value, key);
}

function isMissingOrUndefinedMismatchEqual(
	left: Record<string, unknown>,
	right: Record<string, unknown>,
	key: string,
) {
	const leftHasKey = hasOwnKey(left, key);
	const rightHasKey = hasOwnKey(right, key);

	if (leftHasKey === rightHasKey) {
		return false;
	}

	return leftHasKey ? left[key] === undefined : right[key] === undefined;
}

export function getSearchQueryVariableMismatchFields(
	left: unknown,
	right: unknown,
	path: string = ROOT_PATH,
): string[] {
	if (Object.is(left, right)) {
		return [];
	}

	if (Array.isArray(left) || Array.isArray(right)) {
		if (!Array.isArray(left) || !Array.isArray(right)) {
			return [path];
		}

		const mismatchFields: string[] = [];
		const maxLength = Math.max(left.length, right.length);

		if (left.length !== right.length) {
			mismatchFields.push(`${path}.length`);
		}

		for (let index = 0; index < maxLength; index++) {
			if (!(index in left) || !(index in right)) {
				mismatchFields.push(buildArrayPath(path, index));
				continue;
			}

			mismatchFields.push(
				...getSearchQueryVariableMismatchFields(
					left[index],
					right[index],
					buildArrayPath(path, index),
				),
			);
		}

		return mismatchFields;
	}

	if (isRecord(left) || isRecord(right)) {
		if (!isRecord(left) || !isRecord(right)) {
			return [path];
		}

		const mismatchFields: string[] = [];
		const allKeys = getObjectKeys({ ...left, ...right }).filter(
			(key) => !ALLOWED_DIFF_KEYS.has(key),
		);

		for (const key of allKeys) {
			if (!hasOwnKey(left, key) || !hasOwnKey(right, key)) {
				if (!isMissingOrUndefinedMismatchEqual(left, right, key)) {
					mismatchFields.push(buildPath(path, key));
				}
				continue;
			}

			mismatchFields.push(
				...getSearchQueryVariableMismatchFields(left[key], right[key], buildPath(path, key)),
			);
		}

		return mismatchFields;
	}

	return [path];
}

export function getPreloadedQueryVars({
	cloudId,
	preloadDataProvider,
}: {
	cloudId?: string;
	preloadDataProvider?: SearchPreloadDataProvider;
}) {
	if (!cloudId || !preloadDataProvider) {
		return null;
	}

	const preloadedSearch = preloadDataProvider.getPreloadedSearch({ cloudId });
	const searchResultsQueryVariables = preloadedSearch?.search?.queryVariables ?? null;
	const rightPanelQueryVariables = preloadedSearch?.rightPanel?.queryVariables ?? null;

	if (!searchResultsQueryVariables && !rightPanelQueryVariables) {
		return null;
	}

	return {
		searchResultsQueryVariables,
		rightPanelQueryVariables,
	};
}

export function getPreloadQueryVarsDiff(
	ssrSearchQueryVariables: unknown,
	spaSearchQueryVariables: unknown,
) {
	const mismatchFields = getSearchQueryVariableMismatchFields(
		ssrSearchQueryVariables,
		spaSearchQueryVariables,
	);

	return {
		match: mismatchFields.length === 0,
		mismatchCount: mismatchFields.length,
		mismatchFields,
	};
}

export function getPreloadQueryVarsDiffReport(
	ssrSearchResultsQueryVariables: unknown,
	spaSearchResultsQueryVariables: unknown,
	ssrRightPanelQueryVariables: unknown,
	spaRightPanelQueryVariables: unknown,
): PreloadQueryVarsDiffReport {
	const search = getPreloadQueryVarsDiff(
		ssrSearchResultsQueryVariables,
		spaSearchResultsQueryVariables,
	);
	const rightPanel = getPreloadQueryVarsDiff(
		ssrRightPanelQueryVariables,
		spaRightPanelQueryVariables,
	);
	const mismatchCount = search.mismatchCount + rightPanel.mismatchCount;

	return {
		match: mismatchCount === 0,
		mismatchCount,
		search,
		rightPanel,
	};
}

export type ReportPreloadQueryVarsDiff = ({
	cloudId,
	searchResultsQueryVariables,
	rightPanelQueryVariables,
}: {
	cloudId?: string;
	searchResultsQueryVariables: unknown;
	rightPanelQueryVariables: unknown;
}) => void;

export function usePreloadQueryVarsDiffReporter(
	preloadDataProvider?: SearchPreloadDataProvider,
): ReportPreloadQueryVarsDiff {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const hasReportedRef = useRef(false);

	const firePreloadQueryVarsDiffAnalytics = useCallback(
		(diff: PreloadQueryVarsDiffReport) => {
			const event = onPreloadQueryVarsDiff(diff);
			fireAnalyticsEvent({
				...event,
				attributes: {
					commonAttributes,
					additionalAttributes: event.attributes.additionalAttributes,
				},
			});
		},
		[commonAttributes, fireAnalyticsEvent],
	);

	return useCallback<ReportPreloadQueryVarsDiff>(
		({ cloudId, searchResultsQueryVariables, rightPanelQueryVariables }) => {
			if (hasReportedRef.current) {
				return;
			}

			const preloadedQueryVars = getPreloadedQueryVars({
				cloudId,
				preloadDataProvider,
			});
			if (!preloadedQueryVars) {
				return;
			}

			hasReportedRef.current = true;

			const diff = getPreloadQueryVarsDiffReport(
				preloadedQueryVars.searchResultsQueryVariables,
				searchResultsQueryVariables,
				preloadedQueryVars.rightPanelQueryVariables,
				rightPanelQueryVariables,
			);
			firePreloadQueryVarsDiffAnalytics(diff);
		},
		[firePreloadQueryVarsDiffAnalytics, preloadDataProvider],
	);
}
